// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { IsoCurrencyCode } from '@/currency/constants/iso';

/**
 * Formatting options for currency and number formatting
 * @param placeholder - Placeholder text to display if value is not set
 * @param digits - Number of decimal places to display
 * @param abbreviate - Whether to abbreviate large numbers
 * @param hideZeroDecimals - Whether to hide decimal places for values with no decimal places
 * @param accounting - Whether to use accounting style (negative values in parentheses)
 * @param currencyCode - ISO currency code to denominate in; defaults to USD
 */
interface FormatCurrencyOptions {
  placeholder?: string;
  digits?: number;
  abbreviate?: boolean;
  hideZeroDecimals?: boolean;
  accounting?: boolean;
  currencyCode?: IsoCurrencyCode;
}

interface FormatNumberOptions {
  placeholder?: string;
  digits?: number;
  abbreviate?: boolean;
  accounting?: boolean;
}

// Standard "no value" marker for currency. Null, undefined, and unparseable
// values render this em dash unless a call site overrides `placeholder` (for
// example, form-driven fields that prefer "Not set"). Reserve it for values
// that are genuinely unset — render a skeleton while data is still loading.
const DEFAULT_CURRENCY_PLACEHOLDER = '—';
const DEFAULT_NUMBER_PLACEHOLDER = 'Not set';
export const ACCOUNTING_PADDING = '\u00A0';

/**
 * Formats a number value with optional abbreviation
 * @param value - The numeric value to format
 * @param options - Formatting options
 * @returns Formatted string with optional abbreviation
 */
export function formatNumber(
  value: string | number | null,
  options?: FormatNumberOptions
) {
  if (value === null) {
    return options?.placeholder ?? DEFAULT_NUMBER_PLACEHOLDER;
  }
  if (typeof value === 'string') {
    const numeric = Number(value);
    if (Number.isNaN(numeric)) {
      return options?.placeholder ?? DEFAULT_NUMBER_PLACEHOLDER;
    }
    value = numeric;
  }

  const digits = options?.digits;
  let scale = 1;
  let suffix = '';

  // If abbreviate is requested, calculate the abbreviated value
  if (options?.abbreviate) {
    const absValue = Math.abs(value);

    if (absValue >= 1_000_000_000) {
      scale = 1_000_000_000;
      suffix = 'B';
    } else if (absValue >= 1_000_000) {
      scale = 1_000_000;
      suffix = 'M';
    } else if (absValue >= 1_000) {
      scale = 1_000;
      suffix = 'k';
    }
  }

  // Normalize negative zero to positive zero for display
  const scaledValue = value / scale === 0 ? 0 : value / scale;

  const formatter = Intl.NumberFormat(undefined, {
    useGrouping: true,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

  if (options?.accounting && scaledValue < 0) {
    return `(${formatter.format(Math.abs(scaledValue))}${suffix})`;
  }
  if (options?.accounting) {
    return `${formatter.format(scaledValue)}${suffix}${ACCOUNTING_PADDING}`;
  }

  return formatter.format(scaledValue) + suffix;
}

/**
 * Formats a currency value for display.
 *
 * Null, undefined, and unparseable numeric strings all resolve to the
 * placeholder — by default the em dash ("—"), the standard "no value" marker
 * for currency across tables, detail views, and summary cards. Pass
 * `placeholder` to override the marker (for example "Not set" for form-driven
 * contract values).
 *
 * Do not use the placeholder to represent loading: render a skeleton while data
 * is still being fetched, and reserve the placeholder for values that are
 * genuinely unset.
 *
 * @param value - The value to format, or null/undefined when unset
 * @param options - Formatting options
 * @returns Formatted currency string, or the placeholder when unset/invalid
 */
export function formatCurrency(
  value: string | number | null | undefined,
  options?: FormatCurrencyOptions
) {
  if (value === null || value === undefined) {
    return options?.placeholder ?? DEFAULT_CURRENCY_PLACEHOLDER;
  }
  if (typeof value === 'string') {
    const numeric = Number(value);
    if (Number.isNaN(numeric)) {
      return options?.placeholder ?? DEFAULT_CURRENCY_PLACEHOLDER;
    }
    value = numeric;
  }

  let digits = options?.digits ?? undefined;
  let scale = 1;
  let suffix = '';

  // If abbreviate is requested, calculate the abbreviated value
  if (options?.abbreviate) {
    const absValue = Math.abs(value);

    if (absValue >= 1_000_000_000) {
      scale = 1_000_000_000;
      suffix = 'B';
    } else if (absValue >= 1_000_000) {
      scale = 1_000_000;
      suffix = 'M';
    } else if (absValue >= 1_000) {
      scale = 1_000;
      suffix = 'k';
    } else if (absValue >= 10) {
      if (digits === undefined) {
        digits = 0; // No decimal places for values 10 through 999
      }
    }
  }

  // Format the scaled value with the specified number of decimal places
  // Normalize negative zero to positive zero for display
  const scaledValue = value / scale === 0 ? 0 : value / scale;

  // If hideZeroDecimals is requested, hide decimal places for scaled values with no decimal places
  if (options?.hideZeroDecimals && scaledValue % 1 === 0) {
    digits = 0;
  }
  const formatter = Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: options?.currencyCode ?? 'USD',
    minimumFractionDigits: digits === undefined ? 2 : digits,
    maximumFractionDigits: digits === undefined ? 2 : digits,
  });

  // Accounting style: show negative values in parentheses, positive with padding
  if (options?.accounting) {
    if (scaledValue < 0) {
      return `(${formatter.format(Math.abs(scaledValue))}${suffix})`;
    }
    return `${formatter.format(scaledValue)}${suffix}${ACCOUNTING_PADDING}`;
  }

  return formatter.format(scaledValue) + suffix;
}
